import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Plus, Globe, Check, FolderKanban } from "lucide-react";
import { useProject } from "../context/ProjectContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";

export default function ProjectSwitcher() {
  const { projects, activeProject, switchProject, createProject } = useProject();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", emoji: "🚀", color: "#10b981" });
  const [template, setTemplate] = useState("leer");
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);

  const live = projects.filter((p) => p.status !== "archived");

  const openCreate = async () => {
    setOpen(true);
    try {
      const { data } = await (await import("../lib/api")).api.get("/projects/templates");
      setTemplates(data);
    } catch { /* non-blocking */ }
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error("Projektname fehlt");
      return;
    }
    setSaving(true);
    try {
      await createProject({ ...form, name: form.name.trim(), template });
      toast.success(`Projekt „${form.name}" angelegt`);
      setOpen(false);
      setForm({ name: "", description: "", emoji: "🚀", color: "#10b981" });
      setTemplate("leer");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Projekt konnte nicht angelegt werden");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-3 pt-3" data-testid="project-switcher">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="w-full flex items-center gap-2 rounded-xl px-3 py-2.5 bg-white/[0.03] hover:bg-white/[0.07] border border-white/5 transition text-left"
            data-testid="project-switcher-trigger"
          >
            <span
              className="h-7 w-7 rounded-lg grid place-items-center text-sm shrink-0"
              style={{ background: (activeProject?.color || "#10b981") + "22", color: activeProject?.color || "#10b981" }}
            >
              {activeProject?.emoji || <FolderKanban className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Projekt</div>
              <div className="text-sm font-semibold text-white truncate" data-testid="project-switcher-name">
                {activeProject?.name || "Alle Projekte"}
              </div>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 bg-[#111827] border-white/10 text-slate-200" data-testid="project-switcher-menu">
          <DropdownMenuItem onClick={() => nav("/alle-projekte")} data-testid="switcher-all">
            <Globe className="h-4 w-4 mr-2 text-indigo-400" />
            <span>Alle Projekte (Übersicht)</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-white/5" />
          {live.length === 0 && (
            <div className="px-2 py-3 text-xs text-slate-500">Noch keine Projekte angelegt.</div>
          )}
          {live.map((p) => (
            <DropdownMenuItem
              key={p.id}
              onClick={() => { switchProject(p.id); nav("/"); }}
              data-testid={`switcher-project-${p.id}`}
            >
              <span className="h-5 w-5 rounded grid place-items-center mr-2 text-[11px]" style={{ background: (p.color || "#10b981") + "22", color: p.color }}>
                {p.emoji || "·"}
              </span>
              <span className="flex-1 truncate">{p.name}</span>
              {activeProject?.id === p.id && <Check className="h-3.5 w-3.5 text-emerald-400" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-white/5" />
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); openCreate(); }} data-testid="switcher-new">
            <Plus className="h-4 w-4 mr-2 text-emerald-400" />
            <span>Neues Projekt…</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><span className="hidden" /></DialogTrigger>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-create-dialog">
          <DialogHeader>
            <DialogTitle>Neues Projekt</DialogTitle>
            <DialogDescription className="text-slate-400">
              Wähle eine Vorlage und gib einen Namen — fertig.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Vorlage</div>
              <div className="grid grid-cols-2 gap-2" data-testid="template-grid">
                {(templates.length ? templates : [
                  { key: "leer", label: "Leer", emoji: "📄", description: "Nichts vorbelegt — ich lege alles selbst an.", contents: ["Nichts vorbelegt"] },
                  { key: "ecommerce", label: "E-Commerce-Marke", emoji: "🛍️", description: "Für Online-Shops: Wochenreport, Pipeline, Landingpage-Vorlage.", contents: ["2 Starter-Skills", "1 Landingpage-Vorlage"] },
                  { key: "agentur", label: "Agentur-Kampagne", emoji: "🏢", description: "Für Kundenprojekte: Wettbewerbs-Recherche, Wochen-Report.", contents: ["2 Starter-Skills", "1 Landingpage-Vorlage"] },
                  { key: "creator", label: "Creator / Social", emoji: "🎬", description: "Für Creator: Content-Kalender, Trend-Scanner.", contents: ["2 Starter-Skills", "1 Landingpage-Vorlage"] },
                ]).map((t) => (
                  <button type="button" key={t.key}
                    onClick={() => setTemplate(t.key)}
                    data-testid={`template-${t.key}`}
                    className={`text-left p-3 rounded-lg border transition ${
                      template === t.key
                        ? "border-emerald-400/60 bg-emerald-500/10"
                        : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                    }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{t.emoji}</span>
                      <div className="text-sm font-semibold text-white">{t.label}</div>
                      {template === t.key && <Check className="h-3.5 w-3.5 text-emerald-400 ml-auto" />}
                    </div>
                    <div className="text-[11px] text-slate-400 leading-snug mb-2">{t.description}</div>
                    {t.contents && t.contents.length > 0 && (
                      <div className="text-[10px] text-slate-500 border-t border-white/5 pt-1.5" data-testid={`template-contents-${t.key}`}>
                        <span className="text-emerald-400/70">Enthält:</span> {t.contents.join(" · ")}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
              <Input
                className="w-16 bg-transparent border-white/10 text-center text-xl"
                value={form.emoji}
                maxLength={2}
                onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                data-testid="project-create-emoji"
                aria-label="Emoji"
              />
              <Input
                className="bg-transparent border-white/10"
                placeholder="Projektname (z. B. Sommerkollektion)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="project-create-name"
                required
              />
            </div>

            <details className="group">
              <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200 select-none" data-testid="project-create-advanced">Erweitert</summary>
              <div className="mt-2 space-y-2">
                <Textarea
                  className="bg-transparent border-white/10"
                  placeholder="Kurze Beschreibung (optional)"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="project-create-description"
                />
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>Farbe</span>
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="h-8 w-14 rounded bg-transparent border border-white/10 cursor-pointer"
                    data-testid="project-create-color"
                  />
                </div>
              </div>
            </details>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setOpen(false)} className="border-white/10 bg-transparent text-slate-300">Abbrechen</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="project-create-submit">
                {saving ? "Speichere…" : "Projekt erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
